import { useCallback, useEffect, useMemo, useState } from 'react';
import { classesAPI, departmentsAPI, subjectsAPI, usersAPI } from '../services/api';
import { BookOpen, FileSpreadsheet, Pencil, Plus, Trash2, UserRoundCheck, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { getEmploymentTypeLabel, getWorkArrangementLabel } from '../utils/employment';

const emptyClassForm = {
  name: '',
  department_id: '',
  teacher_id: '',
  schedule_day: '',
  start_time: '',
  end_time: '',
  student_ids: [],
  subject_assignments: [{ subject_id: '', teacher_id: '' }],
};

const scheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Classes = () => {
  const { hasPermission, terminology } = useAuth();
  const [classes, setClasses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [form, setForm] = useState(emptyClassForm);
  const [importSectionId, setImportSectionId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const loadData = useCallback(async () => {
    const [classesRes, departmentsRes, usersRes, subjectsRes] = await Promise.all([
      classesAPI.getAll(),
      departmentsAPI.getAll(),
      usersAPI.getAll(),
      subjectsAPI.getAll(),
    ]);

    setClasses(classesRes.data.classes || []);
    setDepartments(departmentsRes.data.departments || []);
    setUsers(usersRes.data.users || []);
    setSubjects(subjectsRes.data.subjects || []);
  }, []);

  useEffect(() => {
    loadData()
      .catch(() => toast.error(`Failed to load ${terminology.groups.toLowerCase()}`))
      .finally(() => setLoading(false));
  }, [loadData, terminology]);

  const teachers = useMemo(
    () => users.filter((user) => user.role === 'teacher' && user.is_active),
    [users]
  );

  const students = useMemo(
    () => users.filter((user) => user.role === 'student' && user.is_active),
    [users]
  );

  const departmentTeachers = useMemo(() => {
    const selectedDepartmentId = String(form.department_id || '');
    return teachers.filter((teacher) => (
      !selectedDepartmentId || String(teacher.department_id || '') === selectedDepartmentId
    ));
  }, [teachers, form.department_id]);

  const sectionStudentOptions = useMemo(() => {
    const selectedDepartmentId = String(form.department_id || '');

    return students.filter((student) => {
      const sameDepartment = !selectedDepartmentId || String(student.department_id || '') === selectedDepartmentId;
      const unassignedOrCurrent = !student.section_id || form.student_ids.includes(student.id);
      return sameDepartment && unassignedOrCurrent;
    });
  }, [students, form.department_id, form.student_ids]);

  const sectionSubjectOptions = useMemo(() => {
    const selectedDepartmentId = String(form.department_id || '');

    return subjects.filter((subject) => (
      subject.is_active && (!selectedDepartmentId || String(subject.department_id || '') === selectedDepartmentId)
    ));
  }, [subjects, form.department_id]);

  const resetForm = () => {
    setForm(emptyClassForm);
    setEditingSectionId(null);
    setShowForm(false);
  };

  const buildPayload = () => ({
    ...form,
    department_id: form.department_id || null,
    teacher_id: form.teacher_id || null,
    subject_assignments: (form.subject_assignments || [])
      .filter((assignment) => assignment.subject_id)
      .map((assignment) => ({
        subject_id: Number(assignment.subject_id),
        teacher_id: assignment.teacher_id ? Number(assignment.teacher_id) : null,
      })),
  });

  const handleSaveClass = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error('Section name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();

      if (editingSectionId) {
        await classesAPI.update(editingSectionId, payload);
        await classesAPI.updateEnrollments(editingSectionId, { student_ids: form.student_ids });
        toast.success(`${terminology.group} updated successfully`);
      } else {
        await classesAPI.create(payload);
        toast.success(`${terminology.group} created successfully`);
      }

      resetForm();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const startEditSection = (section) => {
    setEditingSectionId(section.id);
    setForm({
      name: section.name || '',
      department_id: section.department_id || '',
      teacher_id: section.teacher_id || '',
      schedule_day: section.schedule_day || '',
      start_time: section.start_time || '',
      end_time: section.end_time || '',
      student_ids: (section.enrolled_students || []).map((student) => student.id),
      subject_assignments: (section.subject_assignments || []).length > 0
        ? section.subject_assignments.map((assignment) => ({
            subject_id: String(assignment.subject_id || ''),
            teacher_id: String(assignment.teacher_id || ''),
          }))
        : [{ subject_id: '', teacher_id: '' }],
    });
    setShowForm(true);
  };

  const handleEnrollmentToggle = (classId, studentId, currentIds) => {
    const nextIds = currentIds.includes(studentId)
      ? currentIds.filter((id) => id !== studentId)
      : [...currentIds, studentId];

    classesAPI.updateEnrollments(classId, { student_ids: nextIds })
      .then(async () => {
        toast.success(`${terminology.group} roster updated`);
        await loadData();
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || 'Failed to update team roster');
      });
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm(`Delete this ${terminology.group.toLowerCase()}? Linked ${terminology.schedules.toLowerCase()}, ${terminology.areas.toLowerCase()}, and assignments will also be removed.`)) {
      return;
    }

    try {
      await classesAPI.remove(sectionId);
      toast.success(`${terminology.group} deleted successfully`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete team');
    }
  };

  const updateSubjectAssignment = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      subject_assignments: prev.subject_assignments.map((assignment, assignmentIndex) => (
        assignmentIndex === index
          ? { ...assignment, [field]: value }
          : assignment
      )),
    }));
  };

  const addSubjectAssignmentRow = () => {
    setForm((prev) => ({
      ...prev,
      subject_assignments: [...prev.subject_assignments, { subject_id: '', teacher_id: '' }],
    }));
  };

  const removeSubjectAssignmentRow = (index) => {
    setForm((prev) => {
      const nextAssignments = prev.subject_assignments.filter((_, assignmentIndex) => assignmentIndex !== index);
      return {
        ...prev,
        subject_assignments: nextAssignments.length > 0 ? nextAssignments : [{ subject_id: '', teacher_id: '' }],
      };
    });
  };

  const handleImportWorkbook = async (event) => {
    event.preventDefault();

    if (!importSectionId || !importFile) {
      toast.error('Choose a team and Excel file');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await classesAPI.importWorkbook(importSectionId, formData);
      const summary = response.data.summary || {};

      toast.success(
        `Imported ${summary.students_imported || 0} employees, ${summary.teachers_imported || 0} supervisors, ${summary.subjects_imported || 0} work areas`
      );

      setImportSectionId('');
      setImportFile(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to import workbook');
    } finally {
      setImporting(false);
    }
  };

  const canManage = hasPermission('sections.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{terminology.groups}</h1>
          <p className="text-gray-500 text-sm mt-1">Organize each {terminology.group.toLowerCase()}, map {terminology.areas.toLowerCase()} and {terminology.leaders.toLowerCase()}, and manage its roster.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : `Add ${terminology.group}`}
          </button>
        )}
      </div>

      {canManage && (
        <form onSubmit={handleImportWorkbook} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-emerald-50 p-2 text-emerald-600">
              <FileSpreadsheet size={18} />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900">Import {terminology.group} Workbook</h2>
              <p className="mt-1 text-sm text-gray-500">
                Upload one Excel file per team. Existing workbook sheet names are supported for compatibility.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Use columns like full_name, email, employee_id, phone, subject_name, subject_code, teacher_email, or teacher_employee_id.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.group}</label>
              <select
                value={importSectionId}
                onChange={(e) => setImportSectionId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select team</option>
                {classes.map((section) => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Excel File</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm"
              />
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={importing}
                className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {importing ? 'Importing...' : 'Import Workbook'}
              </button>
            </div>
          </div>
        </form>
      )}

      {showForm && canManage && (
        <form onSubmit={handleSaveClass} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.group} Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Operations Team - A"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value, teacher_id: '', student_ids: [], subject_assignments: [{ subject_id: '', teacher_id: '' }] })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.group} {terminology.leader}</label>
            <select
              value={form.teacher_id}
              onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select supervisor</option>
              {departmentTeachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Schedule Day</label>
            <select
              value={form.schedule_day}
              onChange={(e) => setForm({ ...form, schedule_day: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select day</option>
              {scheduleDays.map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Start Time</label>
            <input
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">End Time</label>
            <input
              type="time"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">{terminology.area} and {terminology.leader} Mapping</label>
              <button
                type="button"
                onClick={addSubjectAssignmentRow}
                className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
              >
                Add {terminology.area} Mapping
              </button>
            </div>

            <div className="space-y-3 rounded-lg border border-gray-200 p-3">
              {form.subject_assignments.map((assignment, index) => (
                <div key={`assignment-${index}`} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 p-3 md:grid-cols-[1fr_1fr_auto]">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{terminology.area}</label>
                    <select
                      value={assignment.subject_id}
                      onChange={(e) => updateSubjectAssignment(index, 'subject_id', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select work area</option>
                      {sectionSubjectOptions.map((subject) => (
                        <option key={subject.id} value={subject.id}>{subject.name}{subject.code ? ` (${subject.code})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{terminology.leader}</label>
                    <select
                      value={assignment.teacher_id}
                      onChange={(e) => updateSubjectAssignment(index, 'teacher_id', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select supervisor</option>
                      {departmentTeachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeSubjectAssignmentRow(index)}
                      className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <label className="mb-2 block text-sm font-medium text-gray-700">Assign {terminology.people}</label>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                {sectionStudentOptions.map((student) => (
                  <label key={student.id} className="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.student_ids.includes(student.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          student_ids: checked
                            ? [...prev.student_ids, student.id]
                            : prev.student_ids.filter((id) => id !== student.id),
                        }));
                      }}
                    />
                    <span>{student.full_name}</span>
                  </label>
                ))}
              </div>
              {sectionStudentOptions.length === 0 && (
                <div className="py-6 text-center text-sm text-gray-400">
                  No available employees for this department
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">Only active employees from the selected department who are not already placed in another team appear here.</p>
            <p className="mt-1 text-xs text-gray-500">Selected strength: {form.student_ids.length}</p>
          </div>

          <div className="md:col-span-2 xl:col-span-3 flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : editingSectionId ? `Update ${terminology.group}` : `Create ${terminology.group}`}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-400">Loading teams...</div>
        ) : classes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white p-8 text-center text-gray-400">
            No teams created yet
          </div>
        ) : (
          classes.map((classItem) => {
            const enrolledIds = (classItem.enrolled_students || []).map((student) => student.id);

            return (
              <div key={classItem.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <BookOpen size={18} className="text-blue-600" />
                      <h2 className="text-lg font-semibold text-gray-900">{classItem.name}</h2>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {classItem.department_name || 'No department'} • {classItem.teacher_name || 'No team supervisor assigned'}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {classItem.schedule_day || 'No day'} {classItem.start_time ? `• ${classItem.start_time}` : ''} {classItem.end_time ? `to ${classItem.end_time}` : ''}
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700">
                    <Users size={16} />
                    Strength: {classItem.strength}
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-gray-200 p-3">
                  <p className="mb-2 text-sm font-medium text-gray-700">{terminology.area} Mapping</p>
                  {(classItem.subject_assignments || []).length === 0 ? (
                    <div className="text-sm text-gray-400">No work-area mapping set for this team yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {classItem.subject_assignments.map((assignment) => (
                        <div key={`${classItem.id}-${assignment.subject_id}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{assignment.subject_name}</p>
                            <p className="text-xs text-gray-500">{assignment.subject_code || 'No code'}</p>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <UserRoundCheck size={14} className="text-emerald-600" />
                            {assignment.teacher_name || `${terminology.leader} not assigned`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  {canManage ? (
                    <>
                      <button
                        onClick={() => startEditSection(classItem)}
                        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil size={12} />
                        Edit {terminology.group}
                      </button>
                      <button
                        onClick={() => handleDeleteSection(classItem.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">View only</span>
                  )}
                </div>

                <div className="mt-4">
                  <p className="mb-2 text-sm font-medium text-gray-700">{terminology.people}</p>
                  <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 p-3">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {(classItem.enrolled_students || []).length === 0 ? (
                        <div className="text-sm text-gray-400">No employees assigned to this team</div>
                      ) : (
                        classItem.enrolled_students.map((student) => (
                          <div key={`${classItem.id}-${student.id}`} className="rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-700">
                            <div className="font-medium text-gray-900">{student.full_name}</div>
                            <div className="text-xs text-gray-500">{student.employee_id || student.email || terminology.person}</div>
                            <div className="mt-1 text-xs text-slate-400">
                              {getEmploymentTypeLabel(student.employment_type)} · {getWorkArrangementLabel(student.work_arrangement)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  {canManage && (classItem.enrolled_students || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {classItem.enrolled_students.map((student) => (
                        <button
                          key={`remove-${classItem.id}-${student.id}`}
                          onClick={() => handleEnrollmentToggle(classItem.id, student.id, enrolledIds)}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Remove {student.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Classes;
